import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    ConversationHeader,
    MessageSeparator
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import SockJS from 'sockjs-client';
import { Client, IMessage } from '@stomp/stompjs';
import { ChatRepository } from 'repository/chatRepository';
import './ChatRoom.css';

// 커스텀 아바타 컴포넌트
const CustomAvatar = ({ name, size = 'medium' }: { name: string; size?: 'small' | 'medium' }) => {
    const initial = name ? name.charAt(0).toUpperCase() : '?';
    const colors = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
        '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
    ];
    const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
    const sizeStyle = size === 'small' ? { width: 32, height: 32, fontSize: 14 } : { width: 42, height: 42, fontSize: 18 };

    return (
        <div
            className="custom-avatar"
            style={{ backgroundColor: colors[colorIndex], ...sizeStyle }}
        >
            {initial}
        </div>
    );
};

interface ChatMessage {
    messageId: number;
    senderId: string;
    senderNickname: string;
    senderProfileUrl?: string;
    content: string;
    messageType: 'TEXT' | 'IMAGE' | 'SYSTEM';
    createdAt: string;
}

interface RoomInfo {
    roomId: string;
    roomName: string;
    roomType: 'MEETING' | 'DIRECT';
    participantCount: number;
    meetingId?: string;
}

export const ChatRoom = () => {
    const navigate = useNavigate();
    const { roomId } = useParams<{ roomId: string }>();
    const stompClientRef = useRef<Client | null>(null);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const currentUserId = sessionStorage.getItem('userId') || 'mock-user-001';
    const currentUserNickname = sessionStorage.getItem('userName') || '테스트유저';

    // 채팅방 정보 및 메시지 로드
    useEffect(() => {
        if (!roomId) return;

        const loadChatData = async () => {
            setIsLoading(true);
            try {
                // 채팅방 정보 조회
                const roomResponse = await ChatRepository.getRoomInfo(roomId);
                if (roomResponse?.data) {
                    setRoomInfo(roomResponse.data);
                }

                // 채팅 메시지 조회
                const messagesResponse = await ChatRepository.getMessages(roomId, { page: 1, pageSize: 50 });
                if (messagesResponse?.data?.list) {
                    setMessages(messagesResponse.data.list.reverse());
                }
            } catch (error) {
                console.error('채팅 데이터 로드 실패:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadChatData();
    }, [roomId]);

    // WebSocket 연결
    useEffect(() => {
        if (!roomId) return;

        // 이미 입장한 방인지 확인 (세션 내 중복 입장 메시지 방지)
        const joinedRoomsKey = 'chat_joined_rooms';
        const joinedRooms: string[] = JSON.parse(sessionStorage.getItem(joinedRoomsKey) || '[]');
        const isAlreadyJoined = joinedRooms.includes(roomId);

        const socket = new SockJS('http://localhost:30090/ws-chat');
        const client = new Client({
            webSocketFactory: () => socket,
            debug: (str) => console.log('STOMP:', str),
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                console.log('채팅 WebSocket 연결 성공');
                setIsConnected(true);

                // 채팅방 구독
                client.subscribe(`/topic/chat/${roomId}`, (message: IMessage) => {
                    const newMessage: ChatMessage = JSON.parse(message.body);
                    setMessages(prev => {
                        // 중복 메시지 방지 (messageId 기준)
                        if (prev.some(m => m.messageId === newMessage.messageId)) {
                            return prev;
                        }
                        // 동일 사용자의 연속 입장 메시지 방지
                        if (newMessage.messageType === 'SYSTEM' && newMessage.content.includes('입장')) {
                            const recentJoinMsg = prev.filter(m =>
                                m.messageType === 'SYSTEM' &&
                                m.content.includes('입장') &&
                                m.senderId === newMessage.senderId
                            ).pop();
                            // 5분 이내 동일 사용자 입장 메시지가 있으면 무시
                            if (recentJoinMsg) {
                                const timeDiff = new Date(newMessage.createdAt).getTime() - new Date(recentJoinMsg.createdAt).getTime();
                                if (timeDiff < 5 * 60 * 1000) {
                                    return prev;
                                }
                            }
                        }
                        // createdAt 기준으로 정렬하여 추가
                        const updated = [...prev, newMessage];
                        return updated.sort((a, b) =>
                            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                        );
                    });
                });

                // 입장 알림 (처음 입장하는 경우에만)
                if (!isAlreadyJoined) {
                    client.publish({
                        destination: `/app/chat/${roomId}/join`,
                        body: JSON.stringify({ userId: currentUserId, nickname: currentUserNickname })
                    });
                    // 입장한 방 목록에 추가
                    joinedRooms.push(roomId);
                    sessionStorage.setItem(joinedRoomsKey, JSON.stringify(joinedRooms));
                }
            },
            onStompError: (frame) => {
                console.error('STOMP 오류:', frame);
                setIsConnected(false);
            },
            onWebSocketClose: () => {
                console.log('WebSocket 연결 종료');
                setIsConnected(false);
            }
        });

        client.activate();
        stompClientRef.current = client;

        return () => {
            if (stompClientRef.current) {
                // 퇴장 알림은 보내지 않음 (일시적 나가기와 구분)
                // 실제 모임 퇴장은 별도 처리
                stompClientRef.current.deactivate();
            }
        };
    }, [roomId, currentUserId, currentUserNickname]);

    // 메시지 전송
    const handleSend = (text: string) => {
        if (!text.trim() || !roomId) return;

        const message = {
            senderId: currentUserId,
            senderNickname: currentUserNickname,
            content: text.trim(),
            messageType: 'TEXT'
        };

        if (isConnected && stompClientRef.current) {
            stompClientRef.current.publish({
                destination: `/app/chat/${roomId}/send`,
                body: JSON.stringify(message)
            });
        } else {
            // REST API fallback
            ChatRepository.sendMessage(roomId, message);
        }

        setInputValue('');
    };

    // 날짜 구분자 표시 여부 확인
    const shouldShowDateSeparator = (currentMsg: ChatMessage, prevMsg?: ChatMessage) => {
        if (!prevMsg) return true;
        const currentDate = new Date(currentMsg.createdAt).toDateString();
        const prevDate = new Date(prevMsg.createdAt).toDateString();
        return currentDate !== prevDate;
    };

    // 날짜 포맷
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    };

    // 시간 포맷
    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleBackClick = () => {
        navigate('/chat');
    };

    const handleInfoClick = () => {
        navigate(`/chat/participants/${roomId}`);
    };

    if (isLoading) {
        return (
            <div className="chat-room-loading">
                <div className="loading-spinner"></div>
                <p>채팅방 로딩 중...</p>
            </div>
        );
    }

    return (
        <div className="chatscope-wrapper">
            <MainContainer>
                <ChatContainer>
                    <ConversationHeader>
                        <ConversationHeader.Back onClick={handleBackClick} />
                        <CustomAvatar name={roomInfo?.roomName || '채팅방'} />
                        <ConversationHeader.Content
                            userName={roomInfo?.roomName || '채팅방'}
                            info={`${roomInfo?.participantCount || 0}명 참여중 ${isConnected ? '🟢' : '🔴'}`}
                        />
                        <ConversationHeader.Actions>
                            <button className="info-button" onClick={handleInfoClick}>
                                ℹ️
                            </button>
                        </ConversationHeader.Actions>
                    </ConversationHeader>

                    <MessageList>
                        {messages.map((msg, index) => {
                            const isMe = msg.senderId === currentUserId;
                            const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);

                            return (
                                <div key={msg.messageId || index}>
                                    {showDateSeparator && (
                                        <MessageSeparator content={formatDate(msg.createdAt)} />
                                    )}

                                    {msg.messageType === 'SYSTEM' ? (
                                        <MessageSeparator content={msg.content} />
                                    ) : (
                                        <Message
                                            model={{
                                                message: msg.content,
                                                sentTime: formatTime(msg.createdAt),
                                                sender: msg.senderNickname,
                                                direction: isMe ? 'outgoing' : 'incoming',
                                                position: 'single'
                                            }}
                                            avatarPosition={isMe ? undefined : 'tl'}
                                        >
                                            {!isMe && (
                                                <CustomAvatar name={msg.senderNickname} size="small" />
                                            )}
                                            <Message.Header sender={isMe ? undefined : msg.senderNickname} />
                                            <Message.Footer sentTime={formatTime(msg.createdAt)} />
                                        </Message>
                                    )}
                                </div>
                            );
                        })}
                    </MessageList>

                    <MessageInput
                        placeholder="메시지를 입력하세요..."
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onSend={handleSend}
                        attachButton={true}
                        sendButton={true}
                    />
                </ChatContainer>
            </MainContainer>
        </div>
    );
};
